import { Plus, Minus, X } from 'lucide-react';
import QuestionBlockEditor from './QuestionBlockEditor';
import { genBlockId } from '../../lib/questionForm';
import type { ContainerQuestionBlock, ContainerColumnBlock } from '@eps/shared-types';

const ORANGE = '#FF8C42';
const PANEL_BORDER = '#F5E6D0';
const MIN_COLUMNS = 1;
const MAX_COLUMNS = 6;

interface ContainerBlockEditorProps {
  block: ContainerQuestionBlock;
  onChange: (block: ContainerQuestionBlock) => void;
  onDelete: () => void;
}

export default function ContainerBlockEditor({
  block,
  onChange,
  onDelete,
}: ContainerBlockEditorProps) {
  const addColumn = () => {
    if (block.columns.length >= MAX_COLUMNS) return;
    onChange({ ...block, columns: [...block.columns, { blocks: [] }] });
  };

  const removeColumn = () => {
    if (block.columns.length <= MIN_COLUMNS) return;
    const last = block.columns[block.columns.length - 1];
    if (last.blocks.length > 0 && !window.confirm('This column has content. Remove it anyway?')) {
      return;
    }
    onChange({ ...block, columns: block.columns.slice(0, -1) });
  };

  const addColumnBlock = (colIdx: number, type: 'text' | 'image') => {
    const next: ContainerColumnBlock =
      type === 'text'
        ? { id: genBlockId(), type: 'text', text: '' }
        : { id: genBlockId(), type: 'image', imageUrl: '' };
    onChange({
      ...block,
      columns: block.columns.map((col, i) =>
        i === colIdx ? { blocks: [...col.blocks, next] } : col,
      ),
    });
  };

  const updateColumnBlock = (colIdx: number, blockIdx: number, updated: ContainerColumnBlock) => {
    onChange({
      ...block,
      columns: block.columns.map((col, i) =>
        i === colIdx ? { blocks: col.blocks.map((b, j) => (j === blockIdx ? updated : b)) } : col,
      ),
    });
  };

  const removeColumnBlock = (colIdx: number, blockIdx: number) => {
    onChange({
      ...block,
      columns: block.columns.map((col, i) =>
        i === colIdx ? { blocks: col.blocks.filter((_, j) => j !== blockIdx) } : col,
      ),
    });
  };

  return (
    <div className="relative rounded-lg border p-2" style={{ borderColor: PANEL_BORDER }}>
      <button
        type="button"
        onClick={onDelete}
        className="absolute right-1.5 top-1.5 text-gray-300 hover:text-gray-500"
        title="Remove container"
      >
        <X size={14} />
      </button>

      <div className="mb-2 flex items-center gap-2 pr-5">
        <span className="text-[11px] font-semibold text-gray-400">Columns</span>
        <button
          type="button"
          onClick={removeColumn}
          disabled={block.columns.length <= MIN_COLUMNS}
          className="rounded-full border p-0.5 disabled:opacity-30"
          style={{ borderColor: PANEL_BORDER }}
        >
          <Minus size={12} />
        </button>
        <span className="text-xs font-bold" style={{ color: ORANGE }}>
          {block.columns.length}
        </span>
        <button
          type="button"
          onClick={addColumn}
          disabled={block.columns.length >= MAX_COLUMNS}
          className="rounded-full border p-0.5 disabled:opacity-30"
          style={{ borderColor: PANEL_BORDER }}
        >
          <Plus size={12} />
        </button>
      </div>

      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: `repeat(${block.columns.length}, 1fr)` }}
      >
        {block.columns.map((col, colIdx) => (
          <div key={colIdx} className="space-y-1.5">
            {col.blocks.map((b, blockIdx) => (
              <QuestionBlockEditor
                key={b.id}
                block={b}
                onChange={(updated) =>
                  updateColumnBlock(colIdx, blockIdx, updated as ContainerColumnBlock)
                }
                onDelete={() => removeColumnBlock(colIdx, blockIdx)}
              />
            ))}
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => addColumnBlock(colIdx, 'text')}
                className="rounded-full border px-2 py-0.5 text-[10px] font-semibold text-gray-500 hover:border-gray-400"
                style={{ borderColor: PANEL_BORDER }}
              >
                + Text
              </button>
              <button
                type="button"
                onClick={() => addColumnBlock(colIdx, 'image')}
                className="rounded-full border px-2 py-0.5 text-[10px] font-semibold text-gray-500 hover:border-gray-400"
                style={{ borderColor: PANEL_BORDER }}
              >
                + Image
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
